import { useState, useCallback } from 'react';
import { t } from '@lingui/core/macro';
import type { CheckResult, Session } from '@transpordiamet/shared';
import { completeUploadAfterCheckConfirmation } from '@/lib/uploadPhoto';

export interface PendingCheckData {
  checkResult: CheckResult;
  sessionId: string;
  photoId: string;
}

interface UseCheckConfirmationOptions {
  onSuccess: (session: Session) => void;
  onCheckRejected: () => void;
}

export interface UseCheckConfirmationReturn {
  // State
  showCheckConfirmation: boolean;
  pendingCheckData: PendingCheckData | null;
  confirming: boolean;

  // Actions
  handleConfirmCheck: () => Promise<void>;
  handleRejectCheck: () => void;
  setPendingCheckData: (data: PendingCheckData | null) => void;
  setShowCheckConfirmation: (show: boolean) => void;
}

/**
 * Hook to manage check confirmation workflow
 * Handles the dialog and API calls when user needs to confirm a failed check (e.g., VIN not detected)
 */
export function useCheckConfirmation(options: UseCheckConfirmationOptions): UseCheckConfirmationReturn {
  const { onSuccess, onCheckRejected } = options;

  const [showCheckConfirmation, setShowCheckConfirmation] = useState(false);
  const [pendingCheckData, setPendingCheckData] = useState<PendingCheckData | null>(null);
  const [confirming, setConfirming] = useState(false);

  /**
   * User confirmed they want to proceed despite check failure or confirmed VIN
   * Confirms the draft photo that was already created
   */
  const handleConfirmCheck = useCallback(async () => {
    if (!pendingCheckData) return;

    setConfirming(true);

    try {
      const result = await completeUploadAfterCheckConfirmation(pendingCheckData.sessionId, pendingCheckData.photoId);

      if (result.success && result.session) {
        // Reset check confirmation state
        setShowCheckConfirmation(false);
        setPendingCheckData(null);
        setConfirming(false);

        // Notify parent component of success
        onSuccess(result.session);
      } else {
        // Upload completion failed
        const errorMessage = result.error || t`Failed to complete upload`;
        setConfirming(false);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('[CheckConfirmation] Confirmation error:', error);
      setConfirming(false);
      throw error;
    }
  }, [pendingCheckData, onSuccess]);

  /**
   * User rejected check, wants to retake photo
   */
  const handleRejectCheck = useCallback(() => {
    // Clear check state
    setShowCheckConfirmation(false);
    setPendingCheckData(null);

    // Notify parent to handle retake (open camera)
    onCheckRejected();
  }, [onCheckRejected]);

  return {
    // State
    showCheckConfirmation,
    pendingCheckData,
    confirming,

    // Actions
    handleConfirmCheck,
    handleRejectCheck,
    setPendingCheckData,
    setShowCheckConfirmation,
  };
}
